import React, { useState, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import {
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  Typography,
  makeStyles,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  Paper,
  CssBaseline,
  AppBar,
  Toolbar,
  IconButton,
  Container,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
} from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import EventIcon from '@material-ui/icons/Event';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import PersonIcon from '@material-ui/icons/Person';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import ScheduleIcon from '@material-ui/icons/Schedule';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CloseIcon from '@material-ui/icons/Close';
import SharedUserDrawer, { DRAWER_WIDTH_LG, DRAWER_WIDTH_XL } from './SharedUserDrawer';
import PatientVideoCallModal from '../../components/VideoCall/PatientVideoCallModal';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './patientCalendar.custom.css';
import { PatientHeaderContent } from '../../Landingpagecomponents/Header/headerPatient';
import * as AuthActions from '../../store/actions/auth';
import {
  FONT_SIZE_CARD_HEADER,
  FONT_SIZE_SECTION_HEADER,
  FONT_SIZE_SECTION_TEXT,
  FONT_SIZE_SUB_TEXT,
} from './data/sharedStyles';

const ACTIVE_VIDEO_CALL_SESSION_KEY = 'virtus.videoCall.active';

const isVideoCallAlreadyActive = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.sessionStorage.getItem(ACTIVE_VIDEO_CALL_SESSION_KEY) === '1';
  } catch (error) {
    return false;
  }
};

const warnIfVideoCallAlreadyActive = () => {
  if (!isVideoCallAlreadyActive()) {
    return false;
  }

  window.alert('A videocall is already open. Close the current call before starting another one.');
  return true;
};

// Brand colors matching mobile app
const BRAND_COLORS = {
  PRIMARY: '#002D55',
  ACCENT: '#fb741a',
  SUCCESS: '#4caf50',
  ERROR: '#d32f2f',
  WARNING: '#ff9800',
  INFO: '#2196f3',
  DISABLED: '#9e9e9e',
  TEXT_TERTIARY: '#666666',
  BORDER: '#e0e0e0',
};

function Copyright() {
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {'Copyright © '}
      <Link color="inherit" href="https://material-ui.com/">
        Virtus Physio | Your Virtual Physiotherapist
      </Link>{' '}
      {new Date().getFullYear()}
      {'.'}
    </Typography>
  );
}


const localizer = momentLocalizer(moment);

const drawerWidth = 178;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  toolbar: {
    paddingRight: 24,
    minHeight: '50px !important',
    maxHeight: 50,
    [theme.breakpoints.up('xl')]: {
      minHeight: '57px !important',
      maxHeight: 57,
    },
  },
  toolbarIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 8px',
    ...theme.mixins.toolbar,
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    height: 50,
    maxHeight: 50,
    boxShadow: '0 2px 4px rgba(40,40,40,0.22), 0 2px 6px rgba(40,40,40,0.28)',
    [theme.breakpoints.up('xl')]: {
      height: 57,
      maxHeight: 57,
    },
  },
  appBarShift: {
    marginLeft: DRAWER_WIDTH_LG,
    width: `calc(100% - ${DRAWER_WIDTH_LG}px)`,
    [theme.breakpoints.up('xl')]: {
      marginLeft: DRAWER_WIDTH_XL,
      width: `calc(100% - ${DRAWER_WIDTH_XL}px)`,
    },
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 0,
  },
  menuButtonHidden: {
    display: 'none',
  },
  title: {
    flexGrow: 1,
  },
  appBarSpacer: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    height: '100vh',
    overflow: 'auto',
  },
  container: {
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  pageBackground: {
    backgroundColor: '#fafdff',
  },
  card: {
    background: '#f7faff',
    borderRadius: 14,
  },
  cardContent: {
    background: '#f7faff',
    borderRadius: 14,
    padding: 0,
  },
  avatar: {
    backgroundColor: '#6a8caf',
    height: 'clamp(20px, 2.2vw, 36px)',
    width: 'clamp(20px, 2.2vw, 36px)',
    marginRight: 0,
    marginLeft: 0,
    marginTop: 0,
  },
  tabsIndicator: {
    backgroundColor: '#FF7F27',
    height: 3,
    borderRadius: 18,
  },
  tabsRoot: {
    backgroundColor: '#f7faff',
    borderRadius: 12,
    marginBottom: theme.spacing(1),
    height: 'clamp(34px, calc(4px + 4.5vw), 44px)',
    overflow: 'hidden',
    boxShadow: '0 4px 16px rgba(0,45,85,0.12)',
  },
  tabsControlRoot: {
    minHeight: 'clamp(34px, calc(4px + 4.5vw), 44px)',
    height: 'clamp(34px, calc(4px + 4.5vw), 44px)',
    '& .MuiTabs-flexContainer': {
      height: '100%',
      alignItems: 'center',
    },
  },
  tabRoot: {
    fontWeight: 600,
    fontSize: 'clamp(0.75rem, 1.04vw, 0.95rem)',
    textTransform: 'none',
    color: '#002D55',
    minHeight: 'clamp(24px, calc(4px + 4.5vw), 34px)',
    height: 'clamp(24px, calc(4px + 4.5vw), 34px)',
    minWidth: 'clamp(60px, 6.9vw, 100px)',
    padding: '0 clamp(4px, 0.7vw, 12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    '&.Mui-selected': {
      color: '#002D55',
    },
    '&:hover': {
      color: '#FF7F27',
    },
  },
  newBookingButton: {
    marginRight: 12,
    //transition: 'background 0.2s',
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    maxHeight: 30,
    fontSize: 'clamp(10px, 0.82vw, 13px)',
    textTransform: 'none',
    zIndex: 2,
    border: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      fontWeight: 'bold',
      maxHeight: 30,
      zIndex: 2,
      border: 'none',
      boxShadow: 'none',
    },
  },
  closeIconButton: {
    //transition: 'background 0.2s',
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    border: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: 'none',
      boxShadow: 'none',
    },
  },
  calendarHeader: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: 16,
    paddingLeft: 10,
    paddingTop: 'clamp(6px, 0.8vw, 10px)',
    paddingBottom: 'clamp(6px, 0.8vw, 10px)',
    borderBottom: '2px solid #fb741a',
  },
  listItem: {
    backgroundColor: '#fff',
    marginBottom: 6,
    borderRadius: 14,
    transition: 'all 0.2s',
    alignItems: 'center',
    flexWrap: 'wrap',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      transform: 'translateY(-1px)',
    },
  },
  listItemPrimary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'clamp(6px, 0.8vw, 8px)',
    flexWrap: 'wrap',
  },
  upcomingItem: {},
  upcomingAvatar: {
    backgroundColor: '#002D55',
  },
  pastItem: {
    opacity: 0.7,
  },
  statusChip: {
    fontWeight: 600,
    textTransform: 'uppercase',
    fontSize: FONT_SIZE_SUB_TEXT,
    flexShrink: 0,
  },
  listAvatar: {
    backgroundColor: '#6a8caf',
    width: 'clamp(28px, 2.6vw, 40px)',
    height: 'clamp(28px, 2.6vw, 40px)',
  },
  scheduled: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
  },
  confirmed: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  completed: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
  },
  cancelled: {
    backgroundColor: '#ffebee',
    color: '#c62828',
  },
  noAppointments: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#757575',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(6),
  },
  orangeFocused: {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#002D55',
    },
  },
  textfieldColor: {
    '& .MuiInputBase-input': {
      color: '#002D55',
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#002D55',
      opacity: 1,
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: '#002D55',
      opacity: 1,
    },
  },
  dialogTitle: {
    color: '#002D55',
    fontWeight: 'bold',
    fontSize: FONT_SIZE_SECTION_HEADER,
    textTransform: 'uppercase',
    marginBottom: 0,
  },
  dialogContent: {
    color: '#002D55',
    fontWeight: 'bold',
    fontSize: 'clamp(14px, 1.3vw, 20px)',
    paddingTop: 5,
  },
}));

const getStatusChipClass = (status, classes) => {
  switch (status) {
    case 'scheduled':
      return `${classes.statusChip} ${classes.scheduled}`;
    case 'confirmed':
      return `${classes.statusChip} ${classes.confirmed}`;
    case 'completed':
      return `${classes.statusChip} ${classes.completed}`;
    case 'cancelled':
      return `${classes.statusChip} ${classes.cancelled}`;
    default:
      return classes.statusChip;
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'confirmed':
      return <CheckCircleIcon style={{ fontSize: 18 }} />;
    case 'cancelled':
      return <CancelIcon style={{ fontSize: 18 }} />;
    case 'scheduled':
      return <ScheduleIcon style={{ fontSize: 18 }} />;
    default:
      return <EventIcon style={{ fontSize: 18 }} />;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'confirmed':
      return '#4CAF50';
    case 'cancelled':
      return '#f44336';
    case 'scheduled':
      return '#4ee63a';
    case 'completed':
      return '#4CAF50';
    case 'no_show':
      return '#FF9800';
    default:
      return '#757575';
  }
};

const isPastAppointment = (appointment) => {
  if (!appointment?.start_datetime) {
    return false;
  }

  return moment(appointment.start_datetime).isBefore(moment());
};

const PatientAppointments = () => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [calendarView, setCalendarView] = useState(Views.WEEK);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedTreatmentId, setSelectedTreatmentId] = useState(null);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
    const [cancellingAppointment, setCancellingAppointment] = useState(false);
    const [videoCallOpen, setVideoCallOpen] = useState(false);
    const [videoCallAppointment, setVideoCallAppointment] = useState(null);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [reschedulingAppointment, setReschedulingAppointment] = useState(false);
  const [newAppointmentDate, setNewAppointmentDate] = useState('');
  const [newAppointmentTime, setNewAppointmentTime] = useState('');
  const [newAppointmentType, setNewAppointmentType] = useState('');
  const [availableTimes, setAvailableTimes] = useState([]);
  const [consultationServices, setConsultationServices] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [newBookingDate, setNewBookingDate] = useState('');
  const [newBookingTime, setNewBookingTime] = useState('');
  const [newBookingType, setNewBookingType] = useState('');
  const [bookingTreatmentId, setBookingTreatmentId] = useState('');
  const [treatmentOptions, setTreatmentOptions] = useState([]);
  const [treatmentsLoading, setTreatmentsLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [fetchingTimes, setFetchingTimes] = useState(false);
  const [rescheduleConfirmHover, setRescheduleConfirmHover] = useState(false);
  const [bookingConfirmHover, setBookingConfirmHover] = useState(false);
  const [rebokeButtonHover, setRebokeButtonHover] = useState(false);
  const [cancelConfirmDialogOpen, setCancelConfirmDialogOpen] = useState(false);
  const [cancelPolicyLoading, setCancelPolicyLoading] = useState(false);
  const [cancelPolicyData, setCancelPolicyData] = useState(null);
  const [cancelPolicyError, setCancelPolicyError] = useState('');
  const [quickRescheduleLoadingKey, setQuickRescheduleLoadingKey] = useState('');
  const [openSuggestionWindow, setOpenSuggestionWindow] = useState('');
  const [pendingQuickRescheduleSuggestion, setPendingQuickRescheduleSuggestion] = useState(null);
  const [quickRescheduleConfirmOpen, setQuickRescheduleConfirmOpen] = useState(false);
  const [bookingWeekOffset, setBookingWeekOffset] = useState(0);
  const [rescheduleWeekOffset, setRescheduleWeekOffset] = useState(0);
  const [appointmentInterval, setAppointmentInterval] = useState(null);
  const [shouldPollInterval, setShouldPollInterval] = useState(true); // Will be set to false for patient users
  const [pendingRescheduleAppointmentId, setPendingRescheduleAppointmentId] = useState('');
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const token = useSelector(state => state.auth.token);
  const isLoggedIn = useSelector(state => state.auth.isLoggedIn);

  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  const handleClickLogout = async () => {
    console.log('[LOGOUT_HANDLER] handleClickLogout called');
    try {
      console.log('[LOGOUT_HANDLER] Dispatching logout action...');
      await dispatch(AuthActions.logout());
      console.log('[LOGOUT_HANDLER] Logout action completed, redirecting...');
    } catch (e) {
      console.error('[LOGOUT_HANDLER] Error during logout:', e);
      dispatch({ type: AuthActions.LOGOUT });
    }
    console.log('[LOGOUT_HANDLER] Redirecting to home...');
    window.location.replace('/');
  };

  const handleSelectEvent = (event) => {
    setSelectedAppointment(event.resource);
    setAppointmentDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setAppointmentDialogOpen(false);
    setSelectedAppointment(null);
    setCancelConfirmDialogOpen(false);
    setCancelPolicyLoading(false);
    setCancelPolicyData(null);
    setCancelPolicyError('');
    setQuickRescheduleLoadingKey('');
    setPendingQuickRescheduleSuggestion(null);
    setQuickRescheduleConfirmOpen(false);
  };

  const handleJoinVideoCall = (appointment = null) => {
    if (warnIfVideoCallAlreadyActive()) {
      return;
    }

    const appointmentToOpen = appointment || selectedAppointment;
    if (!appointmentToOpen) {
      setVideoCallAppointment(null);
      setAppointmentDialogOpen(false);
      setVideoCallOpen(true);
      return;
    }

    if (!token) return;

    setSelectedAppointment(appointmentToOpen);
    setVideoCallAppointment(appointmentToOpen);
    setAppointmentDialogOpen(false);
    setVideoCallOpen(true);
  };

  const handleCloseVideoCall = () => {
    setVideoCallOpen(false);
    setVideoCallAppointment(null);
  };

  const fetchCancelPolicyAndSuggestions = async (appointmentId) => {
    if (!appointmentId || !token) return;

    setCancelPolicyLoading(true);
    setCancelPolicyError('');
    try {
      const response = await fetch(`https://api.virtus-one.com/api/calendar/appointments/${appointmentId}/reschedule-suggestions/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to load cancellation options');
      }

      setCancelPolicyData(payload);
      setOpenSuggestionWindow((currentOpen) => {
        if (currentOpen) return currentOpen;
        const availableSuggestions = Array.isArray(payload?.reschedule_suggestions) ? payload.reschedule_suggestions : [];
        return availableSuggestions.find((suggestion) => suggestion.window === 'later_same_day')?.window
          || availableSuggestions[0]?.window
          || '';
      });
    } catch (error) {
      console.error('[PatientAppointments] Failed to load cancellation policy:', error);
      setCancelPolicyError(error.message || 'Failed to load cancellation options');
    } finally {
      setCancelPolicyLoading(false);
    }
  };

  const handleQuickRescheduleSuggestion = async (windowSuggestion) => {
    if (!selectedAppointment || !token || !windowSuggestion?.suggested_slot) return;

    const slot = windowSuggestion.suggested_slot;
    setQuickRescheduleLoadingKey(windowSuggestion.window || '');
    setCancellingAppointment(true);
    setReschedulingAppointment(true);
    try {
      const cancelResponse = await fetch(`https://api.virtus-one.com/api/calendar/appointments/${selectedAppointment.id}/cancel/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason: 'Rescheduled by patient' }),
      });

      const cancelPayload = await cancelResponse.json();
      if (!cancelResponse.ok) {
        throw new Error(cancelPayload?.error || 'Failed to cancel appointment');
      }

      const rescheduleResponse = await fetch(`https://api.virtus-one.com/api/calendar/appointments/${selectedAppointment.id}/reschedule/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          start_datetime: slot.start_datetime,
          end_datetime: slot.end_datetime,
        }),
      });

      const reschedulePayload = await rescheduleResponse.json();
      if (!rescheduleResponse.ok) {
        throw new Error(reschedulePayload?.error || 'Failed to create replacement appointment');
      }

      handleCloseDialog();
      setCancelConfirmDialogOpen(false);
      fetchAppointments();
      alert('Appointment moved successfully');
    } catch (error) {
      console.error('[PatientAppointments] Failed to quick reschedule appointment:', error);
      alert(error.message || 'Failed to move appointment. Please try again.');
    } finally {
      setCancellingAppointment(false);
      setReschedulingAppointment(false);
      setQuickRescheduleLoadingKey('');
    }
  };

  const handleQuickRescheduleSlot = async (windowSuggestion, slot) => {
    if (!slot) return;

    setPendingQuickRescheduleSuggestion({
      ...windowSuggestion,
      suggested_slot: slot,
    });
    setQuickRescheduleConfirmOpen(true);
  };

  const handleConfirmQuickRescheduleSlot = async () => {
    if (!pendingQuickRescheduleSuggestion) return;

    const suggestion = pendingQuickRescheduleSuggestion;
    setQuickRescheduleConfirmOpen(false);
    setPendingQuickRescheduleSuggestion(null);
    await handleQuickRescheduleSuggestion(suggestion);
  };

  const handleCancelQuickRescheduleConfirmation = () => {
    setQuickRescheduleConfirmOpen(false);
    setPendingQuickRescheduleSuggestion(null);
  };

  const handleCancelAppointment = () => {
    if (isPastAppointment(selectedAppointment)) {
      alert('Past appointments cannot be cancelled.');
      return;
    }

    setCancelConfirmDialogOpen(true);
  };

  const handleConfirmCancelAppointment = async () => {
    if (!selectedAppointment || !token) return;
    if (isPastAppointment(selectedAppointment)) {
      setCancelConfirmDialogOpen(false);
      alert('Past appointments cannot be cancelled.');
      return;
    }
    
    setCancelConfirmDialogOpen(false);
    setCancellingAppointment(true);
    try {
      const response = await fetch(`https://api.virtus-one.com/api/calendar/appointments/${selectedAppointment.id}/cancel/`, {
        method: 'POST',
        headers: { 
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'cancelled' })
      });
    
      if (response.ok) {
        handleCloseDialog();
        fetchAppointments();
        alert('Appointment cancelled successfully');
      } else {
        console.error('[PatientAppointments] Failed to cancel appointment');
        alert('Failed to cancel appointment. Please try again.');
      }
    } catch (error) {
      console.error('[PatientAppointments] Error cancelling appointment:', error);
      alert('Error cancelling appointment. Please try again.');
    } finally {
      setCancellingAppointment(false);
    }
  };

  const getNextNDays = (n) => {
    const days = [];
    for (let i = 0; i < n; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const dateHasAppointments = (dateStr) => {
    return appointments.some((apt) => {
      const aptDate = moment(apt.start_datetime).format('YYYY-MM-DD');
      return aptDate === dateStr && apt.status !== 'cancelled';
    });
  };

  const handleOpenRescheduleDialog = (appointment = selectedAppointment) => {
    if (!appointment) return;
    fetchAppointmentInterval();
    fetchStaffMembers();
    setRescheduleDialogOpen(true);
    // Initialize with current appointment's date
    const currentDate = new Date(appointment.start_datetime || appointment.start);
    const dateStr = moment(currentDate).format('YYYY-MM-DD');
    const timeStr = moment(currentDate).format('HH:00');
    setSelectedAppointment(appointment);
    setNewAppointmentDate(dateStr);
    setNewAppointmentTime(timeStr);
    setNewAppointmentType(appointment.service || '');
    setSelectedStaffId(appointment.staff_member || '');
    // Fetch times for the current date
    fetchAvailableTimes(dateStr, appointment.staff_member || '');
  };

  useEffect(() => {
    if (!pendingRescheduleAppointmentId || rescheduleDialogOpen || !appointments.length) {
      return;
    }

    const appointment = appointments.find((candidate) => String(candidate.id) === String(pendingRescheduleAppointmentId));
    if (!appointment) {
      return;
    }

    handleOpenRescheduleDialog(appointment);
    setPendingRescheduleAppointmentId('');
  }, [appointments, pendingRescheduleAppointmentId, rescheduleDialogOpen]);

  useEffect(() => {
    if (rescheduleDialogOpen || !appointments.length) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const rescheduleAppointmentId = params.get('reschedule_appointment_id');
    if (!rescheduleAppointmentId) {
      return;
    }

    const appointment = appointments.find((candidate) => String(candidate.id) === String(rescheduleAppointmentId));
    if (!appointment) {
      return;
    }

    handleOpenRescheduleDialog(appointment);
    history.replace({ pathname: '/my_appointments', search: '' });
  }, [appointments, history, location.search, rescheduleDialogOpen]);

  useEffect(() => {
    const handleOpenRescheduleFromEvent = (event) => {
      const appointmentId = String(event?.detail?.appointmentId || '').trim();
      if (!appointmentId) return;
      if (rescheduleDialogOpen) return;

      const appointment = appointments.find((candidate) => String(candidate.id) === appointmentId);
      if (appointment) {
        handleOpenRescheduleDialog(appointment);
        return;
      }

      setPendingRescheduleAppointmentId(appointmentId);
    };

    window.addEventListener('patient:open-reschedule-dialog', handleOpenRescheduleFromEvent);
    return () => window.removeEventListener('patient:open-reschedule-dialog', handleOpenRescheduleFromEvent);
  }, [appointments, rescheduleDialogOpen]);

  const handleCloseRescheduleDialog = () => {
    setRescheduleDialogOpen(false);
    setNewAppointmentDate(null);
    setNewAppointmentTime(null);
    setSelectedStaffId('');
    setRescheduleWeekOffset(0);
  };

  const handleRescheduleAppointment = async () => {
    if (!selectedAppointment || !newAppointmentDate || !newAppointmentTime || !token) {
      alert('Please select both date and time');
      return;
    }

    if (!selectedStaffId) {
      alert('Please select a staff member before rescheduling.');
      return;
    }

    setReschedulingAppointment(true);
    try {
      const startDateTime = moment(`${newAppointmentDate} ${newAppointmentTime}`, 'YYYY-MM-DD HH:mm').toDate();
      const selectedSvc = consultationServices.find((s) => String(s.value) === String(newAppointmentType));
      const existingDurationMinutes = selectedAppointment?.start_datetime && selectedAppointment?.end_datetime
        ? moment(selectedAppointment.end_datetime).diff(moment(selectedAppointment.start_datetime), 'minutes')
        : 60;
      const durationMins = selectedSvc?.duration || existingDurationMinutes || 60;
      const endDateTime = new Date(startDateTime.getTime() + durationMins * 60 * 1000);

      const selectedRoomId = resolveAvailableTimeRoomId(availableTimes, newAppointmentTime);
      const isCancelledAppointment = selectedAppointment.status === 'cancelled';

      const payload = {
        start_datetime: startDateTime.toISOString(),
        end_datetime: endDateTime.toISOString(),
      };

      if (isCancelledAppointment) {
        const response = await fetch(`https://api.virtus-one.com/api/calendar/appointments/${selectedAppointment.id}/reschedule/`, {
          method: 'POST',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          handleCloseRescheduleDialog();
          handleCloseDialog();
          fetchAppointments();
          window.dispatchEvent(new CustomEvent('patient:appointments-updated'));
          alert('Appointment rescheduled successfully');
        } else {
          let errorMessage = 'Failed to reschedule appointment. Please try again.';
          try {
            const errorData = await response.json();
            errorMessage = `Failed to reschedule appointment: ${JSON.stringify(errorData)}`;
            console.error('[PatientAppointments] Reschedule validation error:', errorData);
          } catch (parseError) {
            console.error('[PatientAppointments] Failed to parse reschedule error:', parseError);
          }
          alert(errorMessage);
        }

        return;
      }

      if (newAppointmentType) {
        payload.service = newAppointmentType;
      }

      if (selectedStaffId) {
        payload.staff_member = selectedStaffId;
      }

      if (selectedRoomId) {
        payload.room = selectedRoomId;
      }

      const response = await fetch(`https://api.virtus-one.com/api/calendar/appointments/${selectedAppointment.id}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        handleCloseRescheduleDialog();
        handleCloseDialog();
        fetchAppointments();
        window.dispatchEvent(new CustomEvent('patient:appointments-updated'));
        alert('Appointment rescheduled successfully');
      } else {
        let errorMessage = 'Failed to reschedule appointment. Please try again.';
        try {
          const errorData = await response.json();
          errorMessage = `Failed to reschedule appointment: ${JSON.stringify(errorData)}`;
          console.error('[PatientAppointments] Reschedule validation error:', errorData);
        } catch (parseError) {
          console.error('[PatientAppointments] Failed to parse reschedule error:', parseError);
        }
        alert(errorMessage);
      }
    } catch (error) {
      console.error('[PatientAppointments] Error rescheduling appointment:', error);
      alert('Error rescheduling appointment. Please try again.');
    } finally {
      setReschedulingAppointment(false);
    }
  };

  const getAppointmentLabel = (appointmentType) => {
    if (!appointmentType) return 'Appointment';
    const normalized = String(appointmentType).toLowerCase();
    if (normalized.includes('video')) return 'Video Consultation';
    if (normalized.includes('follow')) return 'Follow-up';
    if (normalized.includes('assessment')) return 'Assessment';
    if (normalized.includes('treatment')) return 'Treatment Session';
    if (normalized.includes('in_person')) return 'In-Person';
    return 'Appointment';
  };

  const getAppointmentDurationText = () => {
    if (!selectedAppointment?.start_datetime || !selectedAppointment?.end_datetime) return '';
    const start = moment(selectedAppointment.start_datetime);
    const end = moment(selectedAppointment.end_datetime);
    const duration = moment.duration(end.diff(start));
    const minutes = duration.asMinutes();
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const remainingMins = minutes % 60;
    return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours} hour${hours > 1 ? 's' : ''}`;
  };

  const getRescheduleSummary = () => {
    if (!selectedAppointment) return null;

    const appointmentDate = selectedAppointment.start_datetime
      ? moment(selectedAppointment.start_datetime).format('ddd, MMM D, YYYY')
      : 'Unknown date';
    const appointmentTime = selectedAppointment.start_datetime && selectedAppointment.end_datetime
      ? `${moment(selectedAppointment.start_datetime).format('HH:mm')} - ${moment(selectedAppointment.end_datetime).format('HH:mm')}`
      : '';
    const serviceLabel = selectedAppointment.service_name
      || selectedAppointment.service_display_name
      || selectedAppointment.title
      || selectedAppointment.appointment_type?.replace(/_/g, ' ')
      || 'Appointment';
    const staffLabel = selectedAppointment.staff_member_name || 'Staff member';

    return {
      appointmentDate,
      appointmentTime,
      serviceLabel,
      staffLabel,
    };
  };

  const getAvailableTimeValue = (timeOption) => {
    if (typeof timeOption === 'string') {
      return timeOption;
    }

    return timeOption?.time || '';
  };

  const getAvailableTimeRoomId = (timeOption) => {
    if (!timeOption || typeof timeOption === 'string') {
      return '';
    }

    return String(timeOption.room_id || timeOption.room || '');
  };

  const resolveAvailableTimeRoomId = (timeOptions, selectedTime) => {
    const matchingTime = (Array.isArray(timeOptions) ? timeOptions : []).find((timeOption) => getAvailableTimeValue(timeOption) === selectedTime);
    return getAvailableTimeRoomId(matchingTime);
  };

  const fetchConsultationServices = async (staffId) => {
    try {
      const staffParam = (staffId || selectedStaffId) ? `?staff_member=${staffId || selectedStaffId}` : '';
      const response = await fetch(`https://api.virtus-one.com/api/services/bookable/${staffParam}`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        const services = (Array.isArray(data) ? data : data.services || [])
          .filter((service) => service.is_active && service.is_bookable_online)
          .map((service) => ({
            value: service.id,
            label: service.name,
            duration: service.duration_minutes || 60,
            serviceType: service.service_type || '',
            price: service.price,
            currency: service.currency,
          }));
        setConsultationServices(services);
      } else {
        setConsultationServices([]);
      }
    } catch (error) {
      console.error('Failed to fetch consultation services:', error);
      setConsultationServices([]);
    }
  };

  const getTreatmentDisplayName = (treatment) => {
    if (!treatment) return '';
    const diagnosis = treatment.diagnosis || {};
    return treatment.display_name || treatment.name || treatment.diagnosis_title || diagnosis.title || diagnosis.diagnosis || 'Untitled course';
  };

  const resolvePreferredTreatmentId = (treatments, preferredId = '') => {
    if (!Array.isArray(treatments) || treatments.length === 0) {
      return '';
    }

    const normalizedPreferredId = String(preferredId || '');
    if (normalizedPreferredId && treatments.some((treatment) => String(treatment.id) === normalizedPreferredId)) {
      return normalizedPreferredId;
    }

    if (treatments.length === 1) {
      return String(treatments[0].id);
    }

    const recentAppointment = appointments
      .filter((appointment) => appointment.treatment && appointment.status !== 'cancelled')
      .sort((left, right) => new Date(right.start_datetime) - new Date(left.start_datetime))[0];

    const recentTreatmentId = String(recentAppointment?.treatment || '');
    if (recentTreatmentId && treatments.some((treatment) => String(treatment.id) === recentTreatmentId)) {
      return recentTreatmentId;
    }

    return String(treatments[0].id);
  };

  const fetchTreatmentOptions = async (preferredId = '') => {
    if (!token) {
      setTreatmentOptions([]);
      setTreatmentsLoading(false);
      return;
    }

    setTreatmentsLoading(true);
    try {
      const response = await fetch('https://api.virtus-one.com/treatment/user/treatments/', {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load treatments');
      }

      const data = await response.json();
      const treatments = Array.isArray(data) ? data : (data.treatments || data.results || []);
      setTreatmentOptions(treatments);

      const nextTreatmentId = resolvePreferredTreatmentId(treatments, preferredId || bookingTreatmentId);
      setBookingTreatmentId((currentTreatmentId) => currentTreatmentId || nextTreatmentId);
    } catch (error) {
      console.error('[PatientAppointments] Error fetching treatments:', error);
      setTreatmentOptions([]);
    } finally {
      setTreatmentsLoading(false);
    }
  };

  const fetchStaffMembers = async () => {
    const normalizeStaff = (staffList = []) => {
      return staffList
        .map((staff) => {
          const id = staff.user_id || staff.id || staff.staff_id || staff.staff_member;
          if (!id) return null;
          const fullName = `${staff.first_name || ''} ${staff.last_name || ''}`.trim();
          return {
            ...staff,
            id: String(id),
            user_id: String(id),
            name: staff.name || staff.staff_member_name || fullName || staff.email || 'Staff Member',
          };
        })
        .filter(Boolean);
    };

    const uniqueById = (staffList = []) => {
      return staffList.filter(
        (staff, index, arr) => arr.findIndex((item) => String(item.user_id) === String(staff.user_id)) === index
      );
    };

    try {
      let resolvedStaff = [];

      const response = await fetch('https://api.virtus-one.com/profile/my_careprovider_staff/', {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        resolvedStaff = normalizeStaff(data.staff || data.results || []);
      }

      if (resolvedStaff.length === 0) {
        const recipientResponse = await fetch('https://api.virtus-one.com/profile/my_message_recipient/', {
          method: 'GET',
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (recipientResponse.ok) {
          const recipientData = await recipientResponse.json();
          resolvedStaff = normalizeStaff(recipientData?.recipient ? [recipientData.recipient] : []);
        }
      }

      if (resolvedStaff.length === 0) {
        const appointmentDerivedStaff = appointments
          .filter((apt) => apt.staff_member)
          .map((apt) => ({
            staff_member: apt.staff_member,
            staff_member_name: apt.staff_member_name,
          }));
        resolvedStaff = normalizeStaff(appointmentDerivedStaff);
      }

      setStaffMembers(uniqueById(resolvedStaff));
    } catch (error) {
      console.error('Failed to fetch staff members:', error);
      setStaffMembers([]);
    }
  };

  const fetchAvailableTimes = async (dateStr, staffId = selectedStaffId) => {
    const startTime = Date.now();
    setFetchingTimes(true);
    try {
      // Add timestamp to prevent browser caching
      const timestamp = new Date().getTime();
      const staffQuery = staffId ? `&staff_member_id=${encodeURIComponent(staffId)}` : '';
      const response = await fetch(`https://api.virtus-one.com/api/calendar/available-times/?date=${dateStr}${staffQuery}&t=${timestamp}`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableTimes(
          Array.isArray(data.available_time_details) && data.available_time_details.length > 0
            ? data.available_time_details
            : (data.available_times || [])
        );
      } else {
        setAvailableTimes([]);
      }
    } catch (error) {
      console.error('Failed to fetch available times:', error);
      setAvailableTimes([]);
    } finally {
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, 1000 - elapsed);
      setTimeout(() => setFetchingTimes(false), remainingTime);
    }
  };

  const handleRebook = () => {
    if (!selectedAppointment) return;
    fetchAppointmentInterval();
    fetchStaffMembers();
    const currentDate = new Date(selectedAppointment.start_datetime || selectedAppointment.start);
    const dateStr = moment(currentDate).format('YYYY-MM-DD');
    const timeStr = moment(currentDate).format('HH:00');
    setNewAppointmentDate(dateStr);
    setNewAppointmentTime(timeStr);
    setNewAppointmentType(selectedAppointment.service || '');
    const staffId = selectedAppointment.staff_member || '';
    setSelectedStaffId(staffId);
    fetchConsultationServices(staffId);
    fetchAvailableTimes(dateStr, staffId);
    setRescheduleDialogOpen(true);
  };

  const handleNewBooking = () => {
    fetchAppointmentInterval();
    fetchStaffMembers();
    const today = moment().format('YYYY-MM-DD');
    setNewBookingDate(today);
    setNewBookingTime('09:00');
    setNewBookingType('');
    setBookingTreatmentId('');
    setSelectedStaffId('');
    fetchTreatmentOptions('');
    setAvailableTimes([]);
    fetchConsultationServices(''); // fetch all services; will re-filter on staff select
    setBookingDialogOpen(true);
  };

  const handleSelectSlot = (slotInfo) => {
    console.log('[PatientAppointments] Slot clicked - opening CREATE mode');
    
    // If in month view, switch to week view and focus on the clicked date
    if (calendarView === Views.MONTH) {
      setCalendarView(Views.WEEK);
      setCalendarDate(slotInfo.start);
      return;
    }
    
    // User clicked on an empty slot - open create dialog
    fetchStaffMembers();
    fetchConsultationServices('');
    const slotDate = moment(slotInfo.start).format('YYYY-MM-DD');
    const slotTime = moment(slotInfo.start).format('HH:mm');
    setNewBookingDate(slotDate);
    setNewBookingTime(slotTime);
    setNewBookingType('');
    setBookingTreatmentId('');
    setSelectedStaffId('');
    fetchTreatmentOptions('');
    setAvailableTimes([]);
    setBookingDialogOpen(true);
  };

  const handleCreateAppointment = async () => {
    if (!newBookingDate || !newBookingTime || !newBookingType || !bookingTreatmentId || !token) {
      alert('Please fill in all required fields');
      return;
    }

    setBookingLoading(true);
    try {
      const startDateTime = moment(`${newBookingDate} ${newBookingTime}`, 'YYYY-MM-DD HH:mm').toDate();
      // Use the service's duration_minutes if available; fall back to 60 min
      const selectedSvc = consultationServices.find((s) => String(s.value) === String(newBookingType));
      if (!selectedSvc) {
        alert('Please select a valid service.');
        setBookingLoading(false);
        return;
      }
      const durationMins = (selectedSvc && selectedSvc.duration) ? selectedSvc.duration : 60;
      const endDateTime = new Date(startDateTime.getTime() + durationMins * 60 * 1000);
      const selectedRoomId = resolveAvailableTimeRoomId(availableTimes, newBookingTime);

      if (!selectedStaffId) {
        alert('Please select a staff member before booking.');
        return;
      }

      const response = await fetch('https://api.virtus-one.com/api/calendar/appointments/create/', {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          start_datetime: startDateTime.toISOString(),
          end_datetime: endDateTime.toISOString(),
          service: newBookingType,
          treatment: bookingTreatmentId,
          ...(selectedSvc.price != null && { booked_price: Number(selectedSvc.price) }),
          ...(selectedSvc.currency && { booked_currency: String(selectedSvc.currency).toUpperCase() }),
          ...(selectedStaffId && { staff_member: selectedStaffId }),
          ...(selectedRoomId && { room: selectedRoomId }),
        }),
      });

      if (response.ok) {
        setBookingDialogOpen(false);
        setNewBookingDate('');
        setNewBookingTime('');
        setNewBookingType('');
        setBookingTreatmentId('');
        setSelectedStaffId('');
        fetchAppointments();
        window.dispatchEvent(new CustomEvent('patient:appointments-updated'));
        alert('Appointment booked successfully!');
      } else {
        let errorMessage = 'Failed to book appointment. Please try again.';
        try {
          const errorData = await response.json();
          errorMessage = `Failed to book appointment: ${JSON.stringify(errorData)}`;
          console.error('[PatientAppointments] Create validation error:', errorData);
        } catch (parseError) {
          console.error('[PatientAppointments] Failed to parse booking error:', parseError);
        }
        alert(errorMessage);
      }
    } catch (error) {
      console.error('[PatientAppointments] Error creating appointment:', error);
      alert('Error booking appointment. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
    fetchConsultationServices('');
    fetchAppointmentInterval();
    
    // Set up polling to check for interval changes every 15 seconds (only for staff users)
    let intervalId;
    if (shouldPollInterval) {
      intervalId = setInterval(() => {
        fetchAppointmentInterval();
      }, 15000);
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line
  }, [shouldPollInterval]);

  useEffect(() => {
    if (!bookingDialogOpen) {
      setBookingConfirmHover(false);
    }
  }, [bookingDialogOpen]);

  useEffect(() => {
    if (cancelConfirmDialogOpen && selectedAppointment) {
      fetchCancelPolicyAndSuggestions(selectedAppointment.id);
    }

    if (!cancelConfirmDialogOpen) {
      setCancelPolicyLoading(false);
      setCancelPolicyData(null);
      setCancelPolicyError('');
      setQuickRescheduleLoadingKey('');
      setOpenSuggestionWindow('');
    }
    // eslint-disable-next-line
  }, [cancelConfirmDialogOpen, selectedAppointment]);

  useEffect(() => {
    if (!bookingDialogOpen) {
      return;
    }

    if (treatmentOptions.length === 0) {
      const selectedService = consultationServices.find((service) => String(service.value) === String(newBookingType));
      const preferredTreatmentId = selectedService?.serviceType === 'initial_consultation'
        ? String(selectedTreatmentId || '')
        : '';
      void fetchTreatmentOptions(preferredTreatmentId);
      return;
    }

    const selectedService = consultationServices.find((service) => String(service.value) === String(newBookingType));
    const preferredTreatmentId = selectedService?.serviceType === 'initial_consultation'
      ? String(selectedTreatmentId || '')
      : '';
    const nextTreatmentId = resolvePreferredTreatmentId(treatmentOptions, preferredTreatmentId || bookingTreatmentId);
    if (!bookingTreatmentId && nextTreatmentId) {
      setBookingTreatmentId(nextTreatmentId);
    }
    // eslint-disable-next-line
  }, [appointments, bookingDialogOpen, bookingTreatmentId, consultationServices, newBookingType, selectedTreatmentId, treatmentOptions]);

  // Re-fetch services filtered by selected staff whenever it changes inside the booking dialog
  useEffect(() => {
    if (bookingDialogOpen) {
      fetchConsultationServices(selectedStaffId || '');
      setNewBookingType(''); // reset selection so user picks a valid service for this staff
      if (newBookingDate && selectedStaffId) {
        fetchAvailableTimes(newBookingDate, selectedStaffId || '');
      } else {
        setAvailableTimes([]);
      }
    }
    // eslint-disable-next-line
  }, [selectedStaffId, bookingDialogOpen]);

  useEffect(() => {
    if (!rescheduleDialogOpen) {
      setRescheduleConfirmHover(false);
    }
  }, [rescheduleDialogOpen]);

  const fetchAppointments = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('https://api.virtus-one.com/api/calendar/appointments/', {
        headers: { Authorization: `Token ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAppointments(data.appointments || []);
      } else {
        console.error('Failed to fetch appointments');
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
    setIsLoading(false);
  };

  const fetchAppointmentInterval = async () => {
    try {
      const response = await fetch('https://api.virtus-one.com/api/profile/clinic/settings/', {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      // If user is not allowed or has no linked clinic, disable polling and return silently
      if (response.status === 400 || response.status === 404) {
        setShouldPollInterval(false); // Stop polling for this user
        return appointmentInterval;
      }
      
      if (response.ok) {
        const data = await response.json();
        const newInterval = data.appointment_interval || 30;
        
        // If interval changed and times have been fetched, clear them so they'll be re-fetched
        if (appointmentInterval !== null && appointmentInterval !== newInterval && availableTimes.length > 0) {
          console.log(`[PatientAppointments] Appointment interval changed from ${appointmentInterval} to ${newInterval} - clearing cached times`);
          setAvailableTimes([]);
        }
        
        setAppointmentInterval(newInterval);
        return newInterval;
      }
    } catch (error) {
      // Silently handle network errors during polling
      // This can happen if user navigates away or has intermittent connection
    }
    return appointmentInterval;
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const selectedAppointmentIsPast = isPastAppointment(selectedAppointment);
  const selectedAppointmentIsVideoConsultation = selectedAppointment?.appointment_type === 'video_consultation';

  // Separate upcoming and past appointments
  const { upcomingAppointments, pastAppointments } = useMemo(() => {
    const now = new Date();
    const upcoming = appointments
      .filter((apt) => new Date(apt.start_datetime) >= now)
      .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
    const past = appointments
      .filter((apt) => new Date(apt.start_datetime) < now)
      .sort((a, b) => new Date(b.start_datetime) - new Date(a.start_datetime));
    return { upcomingAppointments: upcoming, pastAppointments: past };
  }, [appointments]);

  // Map appointments to calendar events
  const events = useMemo(
    () =>
      appointments.map((appointment) => {
        const start = new Date(appointment.start_datetime);
        const end = new Date(appointment.end_datetime);
        const appointmentLabel = getAppointmentLabel(appointment.appointment_type);
        const patientName = appointment.patient_name || appointment.patient || 'Patient';
        return {
          id: appointment.id,
          title: `${patientName} - ${appointmentLabel}`,
          start,
          end,
          resource: appointment,
          status: appointment.status,
          color: appointment.color || null,
        };
      }),
    [appointments]
  );

  // Style calendar events based on status
  const eventStyleGetter = (event) => {
    let backgroundColor = event.color || '#3174ad';
    let opacity = 1;
    const isSelected = selectedAppointment && event.id === selectedAppointment.id;

    if (event.status === 'cancelled') {
      backgroundColor = '#f44336'; // Red color
      opacity = 0.75; // 25% transparency = 75% opacity
    }

    return {
      style: {
        backgroundColor,
        opacity,
        borderRadius: '4px',
        border: isSelected
          ? '1px solid #FF7F27'
          : event.status === 'cancelled' ? '1px solid #b71c1c' : 'none',
        outline: isSelected ? '1px solid #FF7F27' : 'none',
        outlineOffset: isSelected ? '1px' : undefined,
        boxShadow: isSelected ? '0 0 0 2px rgba(255,127,39,0.35)' : 'none',
        color: event.status === 'cancelled' ? '#7a0000' : '#fff',
        fontWeight: event.status === 'cancelled' ? '700' : 'normal',
        textDecoration: event.status === 'cancelled' ? 'line-through' : 'none',
        textShadow: event.status === 'cancelled' ? '0 1px 0 rgba(255,255,255,0.4)' : 'none',
      },
    };
  };

  const dayPropGetter = (date) => {
    // Check if this date has any appointments
    const dateStr = moment(date).format('YYYY-MM-DD');
    const hasAppointment = appointments.some((apt) => {
      const aptDate = moment(apt.start_datetime).format('YYYY-MM-DD');
      return aptDate === dateStr && apt.status !== 'cancelled';
    });

    if (hasAppointment) {
      return {
        style: {
          position: 'relative',
        },
        className: 'has-appointment',
      };
    }

    return {};
  };

  const renderAppointmentItem = (appointment, isUpcoming = true) => {
    const startDate = new Date(appointment.start_datetime);
    const endDate = new Date(appointment.end_datetime);
    const chipStatus = !isUpcoming && appointment.status !== 'cancelled' ? 'completed' : appointment.status;

    return (
      <ListItem
        key={appointment.id}
        button
        onClick={() => {
          setSelectedAppointment(appointment);
          setAppointmentDialogOpen(true);
        }}
        className={`${classes.listItem} ${isUpcoming ? classes.upcomingItem : classes.pastItem}`}
      >
        <ListItemAvatar style={{ minWidth: 'clamp(36px, 3.5vw, 52px)' }}>
          <Avatar className={classes.listAvatar} style={{ backgroundColor: isUpcoming ? '#002D55' : '#6a8caf' }}>
            <EventIcon style={{ fontSize: 'clamp(14px, 1.3vw, 20px)' }} />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          disableTypography
          primary={
            <Box className={classes.listItemPrimary}>
              <Typography style={{ fontWeight: 600, color: '#002D55', wordBreak: 'break-word', minWidth: 0, flex: 1, fontSize: FONT_SIZE_SECTION_HEADER }}>
                {appointment.title || appointment.appointment_type?.replace(/_/g, ' ') || 'Appointment'}
              </Typography>
              <Chip
                icon={getStatusIcon(chipStatus)}
                label={chipStatus}
                className={getStatusChipClass(chipStatus, classes)}
                size="small"
              />
            </Box>
          }
          secondary={
            <Box mt={0.5}>
              <Box display="flex" alignItems="center" flexWrap="wrap" mb={0} style={{ gap: 4 }}>
                <AccessTimeIcon style={{ fontSize: FONT_SIZE_SECTION_TEXT, marginRight: 4, color: '#FF7F27', flexShrink: 0 }} />
                <Typography variant="body2" color="textSecondary" style={{ wordBreak: 'break-word', fontSize: FONT_SIZE_SUB_TEXT }}>
                  {moment(startDate).format('ddd, MMM D, YYYY')} at {moment(startDate).format('HH:mm')} -{' '}
                  {moment(endDate).format('HH:mm')}
                </Typography>
              </Box>
              {appointment.staff_member_name && (
                <Box display="flex" alignItems="center" flexWrap="wrap" mb={0.5} style={{ gap: 4 }}>
                  <PersonIcon style={{ fontSize: FONT_SIZE_SECTION_TEXT, marginRight: 4, color: '#FF7F27', flexShrink: 0 }} />
                  <Typography variant="body2" color="textSecondary" style={{ wordBreak: 'break-word', fontSize: FONT_SIZE_SUB_TEXT }}>
                    {appointment.staff_member_name}
                    {appointment.staff_member_initials && ` (${appointment.staff_member_initials})`}
                  </Typography>
                </Box>
              )}
              {appointment.patient_notes && (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8, fontStyle: 'italic', wordBreak: 'break-word', fontSize: FONT_SIZE_SUB_TEXT }}>
                  Note: {appointment.patient_notes}
                </Typography>
              )}
            </Box>
          }
        />
      </ListItem>
    );
  };

  const renderListView = () => {
    if (isLoading) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress style={{ color: '#F79500' }} />
        </Box>
      );
    }

    if (appointments.length === 0) {
      return (
        <Box className={classes.noAppointments}>
          <EventIcon style={{ fontSize: 64, color: '#ccc', marginBottom: 16 }} />
          <Typography variant="h6" gutterBottom>
            No appointments scheduled
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Your upcoming appointments will appear here
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        {upcomingAppointments.length > 0 && (
          <Box mb={4}>
            <Typography gutterBottom style={{ color: '#002D55', fontWeight: 600, marginBottom: 16, fontSize: FONT_SIZE_SECTION_HEADER }}>
              Upcoming Appointments ({upcomingAppointments.length})
            </Typography>
            <List>{upcomingAppointments.map((apt) => renderAppointmentItem(apt, true))}</List>
          </Box>
        )}

        {pastAppointments.length > 0 && (
          <Box>
            <Divider style={{ margin: '24px 0' }} />
            <Typography gutterBottom style={{ color: '#757575', fontWeight: 600, marginBottom: 16, fontSize: FONT_SIZE_SECTION_HEADER }}>
              Past Appointments ({pastAppointments.length})
            </Typography>
            <Box style={{ maxHeight: 'clamp(400px, 42vw, 525px)', overflowY: 'auto' }}>
              <List>{pastAppointments.map((apt) => renderAppointmentItem(apt, false))}</List>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderCalendarView = () => {
    if (isLoading) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress style={{ color: '#F79500' }} />
        </Box>
      );
    }

    return (
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 770, background: '#fff', borderRadius: 10, marginTop: 16, padding: 12 }}
        views={[Views.MONTH, Views.WEEK, Views.DAY]}
        view={calendarView}
        onView={setCalendarView}
        date={calendarDate}
        onNavigate={setCalendarDate}
        eventPropGetter={eventStyleGetter}
        dayPropGetter={dayPropGetter}
        selectable
        onSelectEvent={handleSelectEvent}
        onSelectSlot={handleSelectSlot}
        popup
        min={new Date(1970, 1, 1, 5, 30)}
        max={new Date(1970, 1, 1, 21, 0)}
        messages={{
          month: 'Month',
          week: 'Week',
          day: 'Day',
          today: 'Today',
          previous: '<',
          next: '>',
        }}
      />
    );
  };

  // Security check: verify authentication before rendering
  if (!isLoggedIn || !token) {
    window.location.replace('/');
    return <div>Redirecting...</div>;
  }

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar
        style={{ color: '#002D55', backgroundColor: '#FFFFFF' }}
        position="absolute"
        className={clsx(classes.appBar, open && classes.appBarShift)}
      >
        <Toolbar className={classes.toolbar}>
          <PatientHeaderContent
            onLogout={handleClickLogout}
            onProfile={() => history.push('/myaccount?tab=profile')}
            onOpenVideo={handleJoinVideoCall}
            onOpenCheckin={() => {}}
            onTreatmentChange={(treatment) => {
              setSelectedTreatmentId(treatment.id);
            }}
          />
        </Toolbar>
      </AppBar>
      <SharedUserDrawer open={open} onOpen={handleDrawerOpen} onClose={handleDrawerClose} />
      <main className={classes.content} style={{ background: 'linear-gradient(90deg, #dce4eb, #d7d4cf)' }}>
        <div className={classes.appBarSpacer} />
        <Container maxWidth="100%" className={classes.container}>
          <Grid container spacing={0}>
            <Grid item xs={12}>
                <div className={classes.tabsRoot}>
                <Tabs
                  value={tabIndex}
                  onChange={handleTabChange}
                  TabIndicatorProps={{ style: { backgroundColor: '#FF7F27', height: 3, borderRadius: 18 } }}
                  textColor="inherit"
                  classes={{ root: classes.tabsControlRoot }}
                >
                  <Tab
                    label="Calendar View"
                    id="tab-0"
                    aria-controls="tabpanel-0"
                    classes={{ root: classes.tabRoot }}
                  />
                  <Tab
                    label="List View"
                    id="tab-1"
                    aria-controls="tabpanel-1"
                    classes={{ root: classes.tabRoot }}
                  />
                </Tabs>
                </div>
                <Card className={classes.card}>
                  {tabIndex === 0 && (
                    <CardContent className={classes.cardContent}>
                      <Box className={classes.calendarHeader}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ width: '100%' }}>
                          <Box display="flex" alignItems="center">
                            <Typography style={{ color: '#002D55', fontWeight: 'bold', fontSize: FONT_SIZE_CARD_HEADER, marginLeft: 8 }}>
                              Appointment Calendar
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            onClick={handleNewBooking}
                            className={classes.newBookingButton}
                          >
                            + New Booking
                          </Button>
                        </Box>
                      </Box>
                      {renderCalendarView()}
                    </CardContent>
                  )}
                  {tabIndex === 1 && (
                    <CardContent className={classes.cardContent}>
                      <Box className={classes.calendarHeader}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ width: '100%' }}>
                          <Box display="flex" alignItems="center">
                            <Typography style={{ color: '#002D55', fontWeight: 'bold', fontSize: FONT_SIZE_CARD_HEADER, marginLeft: 8 }}>
                              My Appointments
                            </Typography>
                          </Box>
                          <Button
                            variant="contained"
                            onClick={handleNewBooking}
                            className={classes.newBookingButton}
                          >
                            + New Booking
                          </Button>
                        </Box>
                      </Box>
                      <Typography style={{ marginTop: 0, marginLeft: 17, marginBottom: 16 }} color="textSecondary" variant="h7">
                        View your scheduled appointments with care providers.
                        <br />
                      </Typography>
                      <Box style={{ padding: 'clamp(6px, 1.5vw, 16px)' }}>{renderListView()}</Box>
                    </CardContent>
                  )}
                  </Card>
            </Grid>
          </Grid>
        </Container>
        <Box pt={2}>
            <Copyright />
          </Box>
      </main>

          {/* Appointment Details Dialog */}
          <Dialog
            open={appointmentDialogOpen}
            onClose={handleCloseDialog}
            maxWidth="sm"
            fullWidth
            PaperProps={{ style: { borderRadius: 18 } }}
          >
            <DialogTitle style={{ borderBottom: '1px solid #eee', fontWeight: 600, color: '#002D55', fontSize: 18 }}>
              <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography style={{ fontWeight: 600, color: '#002D55', fontSize: 18, textTransform: 'uppercase' }}>Appointment Details</Typography>
                <IconButton
                  onClick={handleCloseDialog}
                  size="small"
                  className={classes.closeIconButton}
                  style={{
                    borderRadius: 18,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent style={{ paddingTop: 0, overflow: 'hidden' }}>
              {selectedAppointment && (
                <Box>
                  {selectedAppointment.status === 'cancelled' && (
                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px',
                        marginBottom: '16px',
                        background: '#ffebee',
                        borderRadius: '8px',
                        gap: 8,
                      }}
                    >
                      <Box style={{ color: '#d32f2f' }}>⚠</Box>
                      <Typography style={{ color: '#d32f2f', fontWeight: 600, fontSize: 14 }}>
                        This appointment has been cancelled
                      </Typography>
                    </Box>
                  )}
                  {selectedAppointment.status !== 'cancelled' && selectedAppointmentIsPast && (
                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px',
                        marginBottom: '16px',
                        background: '#f5f5f5',
                        borderRadius: '8px',
                        gap: 8,
                      }}
                    >
                      <Typography style={{ color: '#666', fontWeight: 600, fontSize: 14 }}>
                        Past appointments can no longer be cancelled or rescheduled.
                      </Typography>
                    </Box>
                  )}

                  {/* Provider */}
                  <Box style={{ marginBottom: 12 }}>
                    <Typography style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Provider
                    </Typography>
                    <Typography style={{ fontSize: 14, color: '#002D55', fontWeight: 500 }}>
                      {selectedAppointment.staff_member_name || 'Staff Member'}
                    </Typography>
                  </Box>

                  {/* Service & Date Row */}
                  <Box style={{ marginBottom: 12 }}>
                    <Box style={{ display: 'flex', gap: 32 }}>
                      <Box style={{ flex: 1 }}>
                        <Typography style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Service
                        </Typography>
                        <Typography style={{ fontSize: 14, color: '#002D55', fontWeight: 500 }}>
                          {getAppointmentLabel(selectedAppointment.appointment_type)}
                        </Typography>
                      </Box>
                      <Box style={{ flex: 1 }}>
                        <Typography style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Date
                        </Typography>
                        <Typography style={{ fontSize: 14, color: '#002D55', fontWeight: 500 }}>
                          {moment(selectedAppointment.start_datetime).format('YYYY-MM-DD')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Time & Amount Row */}
                  <Box style={{ marginBottom: 12 }}>
                    <Box style={{ display: 'flex', gap: 32 }}>
                      <Box style={{ flex: 1 }}>
                        <Typography style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Time
                        </Typography>
                        <Typography style={{ fontSize: 14, color: '#002D55', fontWeight: 500 }}>
                          {moment(selectedAppointment.start_datetime).format('HH:mm')} - {moment(selectedAppointment.end_datetime).format('HH:mm')}
                        </Typography>
                      </Box>
                      <Box style={{ flex: 1 }}>
                        <Typography style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Amount
                        </Typography>
                        <Typography style={{ fontSize: 14, color: '#002D55', fontWeight: 500 }}>
                          {selectedAppointment.booked_currency || 'EUR'} {((selectedAppointment.booked_price == null ? 0 : Number(selectedAppointment.booked_price))).toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Status & Payer Row */}
                  <Box style={{ marginBottom: 12 }}>
                    <Box style={{ display: 'flex', gap: 32 }}>
                      <Box style={{ flex: 1 }}>
                        <Typography style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Status
                        </Typography>
                        <Chip
                          icon={getStatusIcon(selectedAppointment.status)}
                          label={selectedAppointment.status.charAt(0).toUpperCase() + selectedAppointment.status.slice(1)}
                          style={{
                            backgroundColor: getStatusColor(selectedAppointment.status),
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: 11,
                          }}
                          size="small"
                        />
                      </Box>
                      <Box style={{ flex: 1 }}>
                        <Typography style={{ fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Payer
                        </Typography>
                        <Chip
                          label="Patient"
                          style={{
                            backgroundColor: '#9E9E9E',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: 11,
                          }}
                          size="small"
                        />
                      </Box>
                    </Box>
                  </Box>

                  {selectedAppointmentIsVideoConsultation && selectedAppointment.status !== 'cancelled' && !selectedAppointmentIsPast && (
                    <Box style={{ marginTop: 8, marginBottom: 12 }}>
                      <Typography style={{ fontSize: 12, color: '#666', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Video call
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={handleJoinVideoCall}
                        disabled={!token}
                        style={{ backgroundColor: '#1f6feb', color: '#fff', fontWeight: 600, borderRadius: 18 }}
                      >
                        Join Call
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </DialogContent>
            <Box style={{ padding: 16, display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eee' }}>
              {selectedAppointment && selectedAppointment.status !== 'cancelled' && !selectedAppointmentIsPast && (
                <>
                  <Button
                    variant="contained"
                    onClick={handleCancelAppointment}
                    disabled={cancellingAppointment || reschedulingAppointment}
                    style={{ backgroundColor: '#fd483c', color: '#fff', fontWeight: 500, borderRadius: 18 }}
                  >
                    {cancellingAppointment ? 'Cancelling...' : 'Cancel appointment'}
                  </Button>
                  <Box
                    onMouseEnter={() => setRebokeButtonHover(true)}
                    onMouseLeave={() => setRebokeButtonHover(false)}
                  >
                    <Button
                      variant="contained"
                      onClick={handleOpenRescheduleDialog}
                      disabled={reschedulingAppointment || cancellingAppointment}
                      style={{ backgroundColor: rebokeButtonHover ? BRAND_COLORS.ACCENT : BRAND_COLORS.PRIMARY, color: '#fff', fontWeight: 500, borderRadius: 18, transition: 'background-color 0.2s' }}
                    >
                      Reschedule
                    </Button>
                  </Box>
                </>
              )}
              {selectedAppointment && selectedAppointment.status === 'cancelled' && (
                <Box style={{ padding: 16, display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eee' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setBookingDialogOpen(false)}
                    disabled={bookingLoading}
                    style={{ color: '#002D55', borderColor: '#ddd', borderRadius: 18 }}
                  >
                    Cancel
                  </Button>
                  <Box
                    onMouseEnter={() => setRebokeButtonHover(true)}
                    onMouseLeave={() => setRebokeButtonHover(false)}
                  >
                    <Button
                      variant="contained"
                      onClick={handleRebook}
                      disabled={reschedulingAppointment}
                      style={{ backgroundColor: rebokeButtonHover ? BRAND_COLORS.ACCENT : BRAND_COLORS.PRIMARY, color: '#fff', fontWeight: 500, borderRadius: 18, transition: 'background-color 0.2s' }}
                    >
                      Reschedule
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Dialog>

      <PatientVideoCallModal
        open={videoCallOpen}
        appointment={videoCallAppointment}
        authToken={token}
        onClose={handleCloseVideoCall}
        selectedTreatmentId={selectedTreatmentId}
      />

      {/* Reschedule Appointment Dialog */}
      <Dialog open={rescheduleDialogOpen} onClose={handleCloseRescheduleDialog} fullWidth maxWidth="sm" PaperProps={{ style: { borderRadius: 18 } }}>
        <DialogTitle style={{ borderBottom: '1px solid #eee'}}>
          <Typography className={classes.dialogTitle} style={{ fontWeight: 600, color: '#002D55', fontSize: 18 }}>Reschedule Appointment</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 24, overflow: 'auto' }}>
          {selectedAppointment && (
            <Box style={{ marginBottom: 20, padding: '12px 14px', backgroundColor: '#f2f2f2', borderRadius: 12, border: '1px solid #e0e0e0' }}>
              {(() => {
                const summary = getRescheduleSummary();
                if (!summary) return null;

                return (
                  <Box>
                    <Typography style={{ fontSize: 12, color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                      Rescheduling
                    </Typography>
                    <Typography style={{ fontSize: 14, color: '#002D55', fontWeight: 600, marginBottom: 4 }}>
                      {summary.serviceLabel}
                    </Typography>
                    <Typography style={{ fontSize: 13, color: '#4d4d4d', lineHeight: 1.6 }}>
                      {summary.appointmentDate}
                      {summary.appointmentTime ? ` • ${summary.appointmentTime}` : ''}
                      {' • '}
                      {summary.staffLabel}
                    </Typography>
                  </Box>
                );
              })()}
            </Box>
          )}
                {/* Select Staff */}
                <Typography style={{ fontSize: 12, color: '#666', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Staff Member (Optional)
                </Typography>
                <Box style={{ marginBottom: 24, position: 'relative' }}>
                  <ArrowDropDownIcon
                    style={{
                      position: 'absolute',
                      left: 10,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: BRAND_COLORS.ACCENT,
                      pointerEvents: 'none',
                    }}
                  />
                  <select
                    value={selectedStaffId}
                    onChange={(e) => {
                      const staffId = e.target.value;
                      setSelectedStaffId(staffId);
                      setNewAppointmentType('');
                      fetchConsultationServices(staffId);
                      if (newAppointmentDate) {
                        fetchAvailableTimes(newAppointmentDate, staffId);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px 10px 34px',
                      fontSize: 14,
                      border: '1px solid #ddd',
                      borderRadius: 14,
                      fontFamily: 'Roboto, sans-serif',
                      color: '#002D55',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      backgroundImage: 'none',
                    }}
                  >
                    <option value="">Any available staff</option>
                    {staffMembers.map((staff) => (
                      <option key={staff.user_id} value={staff.user_id}>
                        {staff.name || `${staff.first_name || ''} ${staff.last_name || ''}`.trim() || staff.email || 'Staff Member'}
                      </option>
                    ))}
                  </select>
                </Box>
          {/* Select Service */}
          <Typography style={{ fontSize: 12, color: '#666', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Service
          </Typography>
          <Box style={{ marginBottom: 24, position: 'relative' }}>
            <ArrowDropDownIcon
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: BRAND_COLORS.ACCENT,
                pointerEvents: 'none',
              }}
            />
            <select
              value={newAppointmentType}
              onChange={(e) => setNewAppointmentType(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 34px',
                fontSize: 14,
                border: '1px solid #ddd',
                borderRadius: 14,
                fontFamily: 'Roboto, sans-serif',
                color: '#002D55',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage: 'none',
              }}
            >
              <option value="">Select a service</option>
              {consultationServices.length > 0 ? (
                consultationServices.map((service) => (
                  <option key={service.value} value={service.value}>
                    {service.label}
                  </option>
                ))
              ) : (
                <option value="" disabled>
                  No online bookable services available
                </option>
              )}
            </select>
          </Box>


          {/* Select New Date */}
          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <Typography style={{ fontSize: 12, color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Select New Date
            </Typography>
            <Box style={{ display: 'flex', gap: 4 }}>
              <Button
                size="small"
                disabled={rescheduleWeekOffset === 0}
                onClick={() => setRescheduleWeekOffset(o => o - 1)}
                style={{ minWidth: 32, borderRadius: 18, color: rescheduleWeekOffset === 0 ? '#ccc' : '#002D55', border: '1px solid #ddd', padding: '2px 8px' }}
              >‹ Previous week</Button>
              <Button
                size="small"
                onClick={() => setRescheduleWeekOffset(o => o + 1)}
                style={{ minWidth: 32, borderRadius: 18, color: '#002D55', border: '1px solid #ddd', padding: '2px 8px' }}
              >Next week ›</Button>
            </Box>
          </Box>
          <Box style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 8,
            marginBottom: 24,
            padding: 12,
            backgroundColor: '#f5f5f5',
            borderRadius: 18,
          }}>
            {Array.from({ length: 14 }, (_, i) => {
              const date = moment().startOf('week').add(rescheduleWeekOffset * 7 + i, 'days').toDate();
              const dateStr = moment(date).format('YYYY-MM-DD');
              const dayName = moment(date).format('ddd').toUpperCase();
              const dayNum = moment(date).format('D');
              const isSelected = dateStr === newAppointmentDate;
              const hasAppointments = dateHasAppointments(dateStr);
              const isPast = moment(date).isBefore(moment().startOf('day'));
              return (
                <Button
                  key={dateStr}
                  disabled={isPast}
                  onClick={() => {
                    setNewAppointmentDate(dateStr);
                    fetchAvailableTimes(dateStr, selectedStaffId || '');
                  }}
                  style={{
                    backgroundColor: isSelected ? '#002D55' : '#fff',
                    color: isSelected ? '#fff' : isPast ? '#ccc' : '#002D55',
                    border: hasAppointments ? '1px solid #fb741a' : (isSelected ? 'none' : '1px solid #ddd'),
                    padding: '8px 4px',
                    borderRadius: 18,
                    minHeight: 60,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: isSelected ? 600 : 400,
                    cursor: isPast ? 'default' : 'pointer',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    opacity: isPast ? 0.4 : 1,
                  }}
                >
                  <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography style={{ fontSize: 12, fontWeight: 700, lineHeight: 1, color: isSelected ? '#FF7F27' : isPast ? '#ccc' : '#002D55' }}>{dayNum}</Typography>
                    <Typography style={{ fontSize: 10, marginTop: 4, lineHeight: 1, color: isSelected ? '#FFFFFF' : isPast ? '#ccc' : '#002D55' }}>{dayName}</Typography>
                    {hasAppointments && (
                      <Box
                        style={{
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          backgroundColor: isSelected ? '#fff' : '#FF7F27',
                          marginTop: 4,
                        }}
                      />
                    )}
                  </Box>
                </Button>
              );
            })}
          </Box>

          {/* Select New Time */}
          <Typography style={{ fontSize: 12, color: '#666', marginBottom: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Select New Time
          </Typography>
          {fetchingTimes ? (
            <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120, marginBottom: 24 }}>
              <CircularProgress style={{ color: '#002D55' }} size={40} />
            </Box>
          ) : (
            <Box style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              marginBottom: 24,
            }}>
              {(availableTimes && availableTimes.length > 0 ? availableTimes : []).map((time) => {
                const timeValue = getAvailableTimeValue(time);
                return (
                <Button
                  key={timeValue}
                  onClick={() => setNewAppointmentTime(timeValue)}
                  style={{
                    width: 'calc(33.333% - 6px)',
                    backgroundColor: newAppointmentTime === timeValue ? '#002D55' : '#fff',
                    color: newAppointmentTime === timeValue ? '#fff' : '#002D55',
                    border: newAppointmentTime === timeValue ? 'none' : '1px solid #ddd',
                    borderRadius: 18,
                    padding: '10px 8px',
                    fontWeight: newAppointmentTime === timeValue ? 600 : 500,
                    fontSize: 12,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {timeValue}
                </Button>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <Box style={{ padding: 16, display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eee' }}>
          <Button
            variant="outlined"
            onClick={handleCloseRescheduleDialog}
            disabled={reschedulingAppointment}
            style={{ color: '#002D55', borderColor: '#ddd', borderRadius:  18 }}
          >
            Cancel
          </Button>
          <Box
            onMouseEnter={() => setRescheduleConfirmHover(true)}
            onMouseLeave={() => setRescheduleConfirmHover(false)}
          >
            <Button
              variant="contained"
              style={{ backgroundColor: rescheduleConfirmHover ? BRAND_COLORS.ACCENT : BRAND_COLORS.PRIMARY, color: '#fff', borderRadius: 18, transition: 'background-color 0.2s' }}
              onClick={handleRescheduleAppointment}
              disabled={reschedulingAppointment || !newAppointmentDate || !newAppointmentTime || !selectedStaffId}
            >
              {reschedulingAppointment ? 'Rescheduling...' : 'Confirm'}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* New Booking Dialog */}
      <Dialog open={bookingDialogOpen} onClose={() => { setBookingDialogOpen(false); setBookingWeekOffset(0); }} fullWidth maxWidth="sm" PaperProps={{ style: { borderRadius: 18 } }}>
        <DialogTitle style={{ borderBottom: '1px solid #eee' }}>
          <Typography className={classes.dialogTitle} style={{ fontWeight: 600, color: '#002D55', fontSize: 18 }}>Book New Appointment</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 24, overflow: 'auto' }}>
          {/* Select Staff */}
          <Typography style={{ fontSize: 12, color: '#666', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Staff Member (Optional)
          </Typography>
          <Box style={{ marginBottom: 24, position: 'relative' }}>
            <ArrowDropDownIcon
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: BRAND_COLORS.ACCENT,
                pointerEvents: 'none',
              }}
            />
            <select
              value={selectedStaffId}
              onChange={(e) => setSelectedStaffId(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 34px',
                fontSize: 14,
                border: '1px solid #ddd',
                borderRadius: 14,
                fontFamily: 'Roboto, sans-serif',
                color: '#002D55',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage: 'none',
              }}
            >
              <option value="">Any available staff</option>
              {staffMembers.map((staff) => (
                <option key={staff.user_id} value={staff.user_id}>
                  {staff.name || `${staff.first_name || ''} ${staff.last_name || ''}`.trim() || staff.email || 'Staff Member'}
                </option>
              ))}
            </select>
          </Box>

          {/* Select Service */}
          <Typography style={{ fontSize: 12, color: '#666', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Service *
          </Typography>
          <Box style={{ marginBottom: 24, position: 'relative' }}>
            <ArrowDropDownIcon
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: BRAND_COLORS.ACCENT,
                pointerEvents: 'none',
              }}
            />
            <select
              value={newBookingType}
              onChange={(e) => setNewBookingType(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 34px',
                fontSize: 14,
                border: '1px solid #ddd',
                borderRadius: 14,
                fontFamily: 'Roboto, sans-serif',
                color: '#002D55',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage: 'none',
              }}
            >
              <option value="">Select a service</option>
              {consultationServices.length > 0 ? (
                consultationServices.map((service) => (
                  <option key={service.value} value={service.value}>
                    {service.label}
                  </option>
                ))
              ) : (
                <option value="" disabled>
                  No online bookable services available
                </option>
              )}
            </select>
          </Box>

          <Typography style={{ fontSize: 12, color: '#666', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Treatment Course *
          </Typography>
          <Box style={{ marginBottom: 24, position: 'relative' }}>
            <ArrowDropDownIcon
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: BRAND_COLORS.ACCENT,
                pointerEvents: 'none',
              }}
            />
            <select
              value={bookingTreatmentId}
              onChange={(e) => setBookingTreatmentId(e.target.value)}
              disabled={treatmentsLoading}
              style={{
                width: '100%',
                padding: '10px 12px 10px 34px',
                fontSize: 14,
                border: '1px solid #ddd',
                borderRadius: 14,
                fontFamily: 'Roboto, sans-serif',
                color: '#002D55',
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundImage: 'none',
              }}
            >
              <option value="">
                {treatmentsLoading ? 'Loading courses...' : 'Select a course'}
              </option>
              {treatmentOptions.map((treatment) => (
                <option key={treatment.id} value={treatment.id}>
                  {getTreatmentDisplayName(treatment)}
                </option>
              ))}
            </select>
            <Typography style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
              Defaults to your current or most recent course. You can change it before booking.
            </Typography>
          </Box>

          
          {/* Select Date */}
          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <Typography style={{ fontSize: 12, color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Select Date *
            </Typography>
            <Box style={{ display: 'flex', gap: 4 }}>
              <Button
                size="small"
                disabled={bookingWeekOffset === 0}
                onClick={() => setBookingWeekOffset(o => o - 1)}
                style={{ minWidth: 32, borderRadius: 18, color: bookingWeekOffset === 0 ? '#ccc' : '#002D55', border: '1px solid #ddd', padding: '2px 8px' }}
              >‹ Previous week</Button>
              <Button
                size="small"
                onClick={() => setBookingWeekOffset(o => o + 1)}
                style={{ minWidth: 32, borderRadius: 18, color: '#002D55', border: '1px solid #ddd', padding: '2px 8px' }}
              >Next week ›</Button>
            </Box>
          </Box>
          <Box style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 8,
            marginBottom: 24,
            padding: 12,
            backgroundColor: '#f5f5f5',
            borderRadius: 18,
          }}>
            {Array.from({ length: 14 }, (_, i) => {
              const date = moment().startOf('week').add(bookingWeekOffset * 7 + i, 'days').toDate();
              const dateStr = moment(date).format('YYYY-MM-DD');
              const dayName = moment(date).format('ddd').toUpperCase();
              const dayNum = moment(date).format('D');
              const isSelected = dateStr === newBookingDate;
              const hasAppointments = dateHasAppointments(dateStr);
              const isPast = moment(date).isBefore(moment().startOf('day'));
              return (
                <Button
                  key={dateStr}
                  disabled={isPast}
                  onClick={() => {
                    setNewBookingDate(dateStr);
                    fetchAvailableTimes(dateStr, selectedStaffId || '');
                  }}
                  style={{
                    backgroundColor: isSelected ? '#002D55' : '#fff',
                    color: isSelected ? '#fff' : isPast ? '#ccc' : '#002D55',
                    border: hasAppointments ? '1px solid #fb741a' : (isSelected ? 'none' : '1px solid #ddd'),
                    padding: '8px 4px',
                    borderRadius: 18,
                    minHeight: 60,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: isSelected ? 600 : 400,
                    cursor: isPast ? 'default' : 'pointer',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    opacity: isPast ? 0.4 : 1,
                  }}
                >
                  <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography style={{ fontSize: 12, fontWeight: 700, lineHeight: 1, color: isSelected ? '#fb741a' : isPast ? '#ccc' : '#002D55' }}>{dayNum}</Typography>
                    <Typography style={{ fontSize: 10, marginTop: 4, lineHeight: 1, color: isSelected ? '#FFFFFF' : isPast ? '#ccc' : '#002D55' }}>{dayName}</Typography>
                    {hasAppointments && (
                      <Box
                        style={{
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          backgroundColor: isSelected ? '#fff' : '#fb741a',
                          marginTop: 4,
                        }}
                      />
                    )}
                  </Box>
                </Button>
              );
            })}
          </Box>

          {/* Select Time */}
          <Typography style={{ fontSize: 12, color: '#666', marginBottom: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Select Time *
          </Typography>
          {fetchingTimes ? (
            <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120, marginBottom: 24 }}>
              <CircularProgress style={{ color: '#002D55' }} size={40} />
            </Box>
          ) : (
            <Box style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              marginBottom: 24,
            }}>
              {(availableTimes && availableTimes.length > 0 ? availableTimes : []).map((time) => {
                const timeValue = getAvailableTimeValue(time);
                return (
                <Button
                  key={timeValue}
                  onClick={() => setNewBookingTime(timeValue)}
                  style={{
                    width: 'calc(33.333% - 6px)',
                    backgroundColor: newBookingTime === timeValue ? '#002D55' : '#fff',
                    color: newBookingTime === timeValue ? '#fff' : '#002D55',
                    border: newBookingTime === timeValue ? 'none' : '1px solid #ddd',
                    borderRadius: 18,
                    padding: '10px 8px',
                    fontWeight: newBookingTime === timeValue ? 600 : 500,
                    fontSize: 12,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {timeValue}
                </Button>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <Box style={{ padding: 16, display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eee' }}>
          <Button
            variant="outlined"
            onClick={() => setBookingDialogOpen(false)}
            disabled={bookingLoading}
            style={{ color: '#002D55', borderColor: '#ddd', borderRadius: 18 }}
          >
            Cancel
          </Button>
          <Box
            onMouseEnter={() => setBookingConfirmHover(true)}
            onMouseLeave={() => setBookingConfirmHover(false)}
          >
            <Button
              variant="contained"
              style={{ backgroundColor: bookingConfirmHover ? BRAND_COLORS.ACCENT : BRAND_COLORS.PRIMARY, color: '#fff', borderRadius: 18, transition: 'background-color 0.2s' }}
              onClick={handleCreateAppointment}
              disabled={bookingLoading || !newBookingDate || !newBookingTime || !newBookingType || !bookingTreatmentId || !selectedStaffId}
            >
              {bookingLoading ? 'Booking...' : 'Confirm'}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Cancel Appointment Confirmation Dialog */}
      <Dialog open={cancelConfirmDialogOpen} onClose={() => setCancelConfirmDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 18 } }}>
        <DialogTitle style={{ borderBottom: '1px solid #eee', paddingBottom: 16 }}>
          <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography style={{ fontWeight: 600, color: '#002D55', fontSize: 18 }}>
              Cancel Appointment?
            </Typography>
            <IconButton
              onClick={() => setCancelConfirmDialogOpen(false)}
              size="small"
              className={classes.closeIconButton}
              style={{
                borderRadius: 18,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 24, overflow: 'auto' }}>
          <Typography style={{ color: '#666', marginBottom: 16 }}>
            Are you sure you want to cancel this appointment? 
          </Typography>

          {cancelPolicyLoading && (
            <Box style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <CircularProgress size={28} style={{ color: '#002D55' }} />
            </Box>
          )}

          {cancelPolicyError && (
            <Box style={{ backgroundColor: '#f5f5f5', border: '1px solid #ddd', borderRadius: 12, padding: 12, marginBottom: 16 }}>
              <Typography style={{ color: '#002D55', fontWeight: 600, fontSize: 13 }}>
                {cancelPolicyError}
              </Typography>
            </Box>
          )}

          {cancelPolicyData?.is_late_cancellation && (
            <Box style={{ backgroundColor: '#fff7ef', border: '1px solid #ffd8bf', borderRadius: 12, padding: 12, marginBottom: 16 }}>
              <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
                Late cancellation fee may apply
              </Typography>
              <Typography style={{ color: '#5f6c7b', fontSize: 13, lineHeight: 1.5 }}>
                {Number(cancelPolicyData?.cancellation_fee_amount || 0) > 0
                  ? `You may incur a cancelation fee of ${(cancelPolicyData?.cancellation_fee_currency || selectedAppointment?.booked_currency || 'EUR')} ${Number(cancelPolicyData.cancellation_fee_amount).toFixed(2)} if you cancel within ${Number(cancelPolicyData?.late_cancellation_threshold_hours || 24)} hours.`
                  : (cancelPolicyData.cancellation_fee_message || `This cancellation is within ${Number(cancelPolicyData?.late_cancellation_threshold_hours || 24)} hours and may incur a fee.`)}
              </Typography>
            </Box>
          )}

          {(() => {
            const laterSameDaySuggestion = Array.isArray(cancelPolicyData?.reschedule_suggestions)
              ? cancelPolicyData.reschedule_suggestions.find((windowSuggestion) => windowSuggestion.window === 'later_same_day')
              : null;
            const availableSlots = Array.isArray(laterSameDaySuggestion?.available_slots) ? laterSameDaySuggestion.available_slots : [];
            const suggestedSlot = laterSameDaySuggestion?.suggested_slot;
            const hasSlot = Boolean(suggestedSlot?.start_datetime && suggestedSlot?.end_datetime);

            if (!laterSameDaySuggestion) {
              return null;
            }

            return (
              <Box style={{ marginBottom: 16 }}>
                <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
                  Move to later appointment same day without fee
                </Typography>
                <Typography style={{ fontSize: 12, color: '#666', marginBottom: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Select New Time
                </Typography>
                <Box style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  marginBottom: 24,
                }}>
                  {availableSlots.length > 0 ? availableSlots.map((slot) => {
                    const isSelected = pendingQuickRescheduleSuggestion?.suggested_slot?.start_datetime === slot.start_datetime;
                    return (
                      <Button
                        key={slot.start_datetime}
                        variant="outlined"
                        disabled={cancellingAppointment || reschedulingAppointment || quickRescheduleLoadingKey === 'later_same_day'}
                        onClick={() => handleQuickRescheduleSlot(laterSameDaySuggestion, slot)}
                        style={{
                          width: 'calc(33.333% - 6px)',
                          backgroundColor: isSelected ? '#002D55' : '#fff',
                          color: isSelected ? '#fff' : '#002D55',
                          border: isSelected ? 'none' : '1px solid #ddd',
                          borderRadius: 18,
                          padding: '10px 8px',
                          fontWeight: isSelected ? 600 : 500,
                          fontSize: 12,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {moment(slot.start_datetime).format('HH:mm')}
                      </Button>
                    );
                  }) : (
                    <Typography style={{ color: '#5f6c7b', fontSize: 12 }}>
                      No alternate slots available in this window.
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })()}

          <Box style={{ marginBottom: 16, display: 'flex', justifyContent: 'center' }}>
            <Box
              onMouseEnter={() => setRescheduleConfirmHover(true)}
              onMouseLeave={() => setRescheduleConfirmHover(false)}
            >
              <Button
                variant="contained"
                onClick={() => {
                  setCancelConfirmDialogOpen(false);
                  handleOpenRescheduleDialog();
                }}
                style={{
                  borderRadius: 18,
                  backgroundColor: rescheduleConfirmHover ? BRAND_COLORS.ACCENT : BRAND_COLORS.PRIMARY,
                  color: '#fff',
                  fontWeight: 500,
                  transition: 'background-color 0.2s',
                }}
              >
               See more timeslots to reschedule
              </Button>
            </Box>
          </Box>

          <Dialog
            open={quickRescheduleConfirmOpen}
            onClose={handleCancelQuickRescheduleConfirmation}
            fullWidth
            maxWidth="xs"
            PaperProps={{ style: { borderRadius: 18 } }}
          >
            <DialogTitle style={{ borderBottom: '1px solid #eee', paddingBottom: 16 }}>
              <Typography style={{ fontWeight: 600, color: '#002D55', fontSize: 18 }}>
                Confirm reschedule
              </Typography>
            </DialogTitle>
            <DialogContent style={{ paddingTop: 24, overflow: 'auto' }}>
              <Typography style={{ color: '#666', marginBottom: 18 }}>
                Move this appointment to the selected time slot?
              </Typography>
              {pendingQuickRescheduleSuggestion?.suggested_slot && (
                <Box style={{ backgroundColor: '#fbfdff', border: '1px solid #dde6f2', borderRadius: 14, padding: 14, marginBottom: 18 }}>
                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                    {moment(pendingQuickRescheduleSuggestion.suggested_slot.start_datetime).format('dddd, MMMM D, YYYY')}
                  </Typography>
                  <Typography style={{ color: '#5f6c7b', fontSize: 13 }}>
                    {moment(pendingQuickRescheduleSuggestion.suggested_slot.start_datetime).format('HH:mm')} - {moment(pendingQuickRescheduleSuggestion.suggested_slot.end_datetime).format('HH:mm')}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions style={{ padding: 16, display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eee' }}>
              <Button
                onClick={handleCancelQuickRescheduleConfirmation}
                variant="outlined"
                disabled={reschedulingAppointment}
                style={{ color: '#002D55', borderColor: '#ddd', borderRadius: 18, textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Box
                onMouseEnter={() => setRescheduleConfirmHover(true)}
                onMouseLeave={() => setRescheduleConfirmHover(false)}
              >
                <Button
                  onClick={handleConfirmQuickRescheduleSlot}
                  variant="contained"
                  disabled={reschedulingAppointment || !pendingQuickRescheduleSuggestion}
                  style={{ backgroundColor: rescheduleConfirmHover ? BRAND_COLORS.ACCENT : BRAND_COLORS.PRIMARY, color: '#fff', borderRadius: 18, textTransform: 'none', transition: 'background-color 0.2s' }}
                >
                  {reschedulingAppointment ? 'Rescheduling...' : 'Confirm'}
                </Button>
              </Box>
            </DialogActions>
          </Dialog>

          {selectedAppointment && (
            <Box style={{ backgroundColor: '#f5f5f5', padding: 12, borderRadius: 8, marginBottom: 16 }}>
              <Typography style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>
                <strong>Date & Time:</strong> {selectedAppointment.start_datetime ? moment(selectedAppointment.start_datetime).format('MMMM D, YYYY [at] h:mm A') : 'N/A'}
              </Typography>
              {selectedAppointment.staff_member_name && (
                <Typography style={{ fontSize: 14, color: '#666' }}>
                  <strong>Practitioner:</strong> {selectedAppointment.staff_member_name}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <Box style={{ padding: 16, display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eee' }}>
          <Button
            variant="outlined"
            onClick={() => setCancelConfirmDialogOpen(false)}
            disabled={cancellingAppointment}
            style={{ color: '#002D55', borderColor: '#ddd', borderRadius: 18 }}
          >
            Keep Appointment
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCancelAppointment}
            disabled={cancellingAppointment}
            style={{ backgroundColor: '#fd483c', color: '#fff', fontWeight: 500, borderRadius: 18 }}
          >
            {cancellingAppointment ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </Box>
      </Dialog>
    </div>
  );
};

export default PatientAppointments;
